/*
 * Reviewtje: "Bij onze klanten".
 *
 * Rechts een foto die rustig doorloopt, links een Leaflet-kaart met een pin per
 * klant. Alles draait om één index: de platte lijst van foto's (klant 1 foto 1,
 * klant 1 foto 2, klant 2 foto 1, ...). Wie die index verzet, verzet ook de
 * naam, de plaats, de gebruiksregel, de puntjes en de actieve pin.
 *
 * Uitgangspunten:
 *  - Leaflet wordt pas ingeladen als de sectie bijna in beeld komt; staat de
 *    sectie niet op de pagina, dan gebeurt er niets.
 *  - Zonder JavaScript blijft de eerste foto met naam/plaats/regel staan; de
 *    kaart en de slideshow zijn een toevoeging, geen voorwaarde.
 *  - De theme-editor hertekent secties: init is idempotent (data-rv-ready) en
 *    bij shopify:section:unload ruimen we kaart, timers en observers op.
 */
(function () {
  'use strict';

  // Twee keer dezelfde <script src> (de editor herlaadt secties) mag geen
  // tweede set document-listeners opleveren.
  if (window.RvKlantenKaart) {
    window.RvKlantenKaart.initAll();
    return;
  }

  var RESUME_AFTER = 20000; // ms rust na een klik voordat de slideshow doorgaat
  var sections = [];
  var leafletPromise = null;

  var TILES = {
    carto: {
      url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      options: {
        subdomains: 'abcd',
        maxZoom: 19,
        detectRetina: true,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>-bijdragers &copy; <a href="https://carto.com/attributions">CARTO</a>',
      },
    },
    osm: {
      url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      options: {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>-bijdragers',
      },
    },
  };

  /** Leaflet één keer per pagina inladen, hoe vaak de sectie er ook staat. */
  function loadLeaflet(cssUrl, jsUrl) {
    if (window.L && window.L.map) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;

    leafletPromise = new Promise(function (resolve, reject) {
      if (cssUrl && !document.querySelector('link[data-rv-leaflet]')) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        link.setAttribute('data-rv-leaflet', '');
        document.head.appendChild(link);
      }

      var script = document.querySelector('script[data-rv-leaflet]');
      if (!script) {
        script = document.createElement('script');
        script.src = jsUrl;
        script.defer = true;
        script.setAttribute('data-rv-leaflet', '');
        document.head.appendChild(script);
      }

      script.addEventListener('load', function () {
        resolve(window.L);
      });
      script.addEventListener('error', function () {
        leafletPromise = null;
        reject(new Error('Leaflet kon niet worden geladen'));
      });
    });

    return leafletPromise;
  }

  function toNumber(value) {
    var n = parseFloat(String(value).replace(',', '.'));
    return isFinite(n) ? n : null;
  }

  function initSection(root) {
    if (root.dataset.rvReady === 'true') return;
    root.dataset.rvReady = 'true';

    var dataEl = root.querySelector('[data-rv-klanten-data]');
    var photos = Array.prototype.slice.call(root.querySelectorAll('[data-rv-photo]'));
    if (!dataEl || !photos.length) return;

    var customers;
    try {
      customers = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!customers || !customers.length) return;

    var state = {
      root: root,
      photos: photos,
      customers: customers,
      current: 0,
      map: null,
      markers: [],
      timer: null,
      resumeTimer: null,
      observers: [],
      listeners: [],
      inView: true,
      hovered: false,
      userPaused: false,
    };

    var nameEl = root.querySelector('[data-rv-name]');
    var placeEl = root.querySelector('[data-rv-place]');
    var useEl = root.querySelector('[data-rv-use]');
    var infoEl = root.querySelector('[data-rv-info]');
    var navEl = root.querySelector('[data-rv-nav]'); // rij met de puntjes van de huidige klant
    var mapEl = root.querySelector('[data-rv-map]');
    var dotGroups = Array.prototype.slice.call(root.querySelectorAll('[data-rv-dots]'));
    var prevEl = root.querySelector('[data-rv-prev]');
    var nextEl = root.querySelector('[data-rv-next]');

    // De pijlen stappen door ALLE klanten heen, dus zijn nuttig zodra er meer
    // dan één foto in totaal is — ook als de huidige klant er zelf maar één heeft.
    if (photos.length > 1) {
      if (prevEl) prevEl.hidden = false;
      if (nextEl) nextEl.hidden = false;
    }

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var autoplayOn = root.dataset.autoplay === 'true';
    var speed = parseInt(root.dataset.autoplaySpeed, 10) || 5500;

    // Per foto onthouden bij welke klant hij hoort; dat is de enige lookup die
    // we tijdens het wisselen nodig hebben.
    var photoCustomer = photos.map(function (el) {
      return parseInt(el.dataset.customer, 10) || 0;
    });
    var photoIndexInCustomer = photos.map(function (el) {
      return parseInt(el.dataset.photo, 10) || 0;
    });
    var countPerCustomer = customers.map(function (_, i) {
      return photoCustomer.filter(function (c) {
        return c === i;
      }).length;
    });

    function on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      state.listeners.push([target, type, handler, options]);
    }

    /** Pas laden wanneer een foto echt aan de beurt is (of de volgende is). */
    function ensureSource(el) {
      if (!el || el.tagName !== 'IMG' || !el.dataset.src) return;
      if (el.dataset.srcset) el.srcset = el.dataset.srcset;
      el.src = el.dataset.src;
      el.removeAttribute('data-src');
    }

    function activate(index, byUser) {
      if (index < 0 || index >= photos.length) return;
      state.current = index;

      var customerIndex = photoCustomer[index];
      var customer = customers[customerIndex] || {};

      ensureSource(photos[index]);
      ensureSource(photos[(index + 1) % photos.length]);

      photos.forEach(function (el, i) {
        var active = i === index;
        el.classList.toggle('is-active', active);
        if (active) {
          el.removeAttribute('aria-hidden');
        } else {
          el.setAttribute('aria-hidden', 'true');
        }
      });

      if (nameEl) nameEl.textContent = customer.name || '';
      if (placeEl) placeEl.textContent = customer.city || '';
      if (useEl) useEl.textContent = customer.usage || '';

      var count = countPerCustomer[customerIndex] || 0;
      if (navEl) navEl.hidden = count < 2;

      dotGroups.forEach(function (group) {
        var groupIndex = parseInt(group.dataset.rvDots, 10);
        var isCurrent = groupIndex === customerIndex;
        group.hidden = !isCurrent || count < 2;
        if (!isCurrent) return;
        Array.prototype.forEach.call(group.querySelectorAll('[data-rv-dot]'), function (dot) {
          var dotIndex = parseInt(dot.dataset.rvDot, 10) || 0;
          // Meer puntjes dan foto's kan alleen bij een slordige lijst met
          // bestandsnamen; die puntjes horen dan niet zichtbaar te zijn.
          dot.hidden = dotIndex >= count;
          dot.setAttribute('aria-current', dotIndex === photoIndexInCustomer[index] ? 'true' : 'false');
        });
      });

      state.markers.forEach(function (marker, i) {
        var el = marker.getElement();
        if (!el) return;
        var active = i === customerIndex;
        el.classList.toggle('is-active', active);
        el.setAttribute('aria-current', active ? 'true' : 'false');
        marker.setZIndexOffset(active ? 1000 : 0);
      });

      if (byUser) pauseForUser();
    }

    /**
     * Eén stap door de hele reeks foto's van alle klanten heen (wrapt rond).
     * Bij de laatste foto van een klant gaat "volgende" dus door naar de
     * eerste foto van de volgende klant, en die klant wordt actief op de
     * kaart — precies zoals een klik op zijn pin dat zou doen.
     */
    function stepGlobal(delta) {
      if (photos.length < 2) return;
      var index = (state.current + delta + photos.length) % photos.length;
      activate(index, true);
    }

    function selectCustomer(customerIndex) {
      var first = photoCustomer.indexOf(customerIndex);
      if (first === -1) return;
      activate(first, true);
    }

    // ------------------------------------------------------------ autoplay

    function canAutoplay() {
      return (
        autoplayOn &&
        !reduceMotion.matches &&
        photos.length > 1 &&
        state.inView &&
        !state.hovered &&
        !state.userPaused &&
        !document.hidden
      );
    }

    function sync() {
      if (state.timer) {
        clearInterval(state.timer);
        state.timer = null;
      }
      if (!canAutoplay()) {
        // Staat de slideshow stil, dan mag een schermlezer de wissel wél melden.
        if (infoEl) infoEl.setAttribute('aria-live', 'polite');
        return;
      }
      if (infoEl) infoEl.setAttribute('aria-live', 'off');
      state.timer = setInterval(function () {
        activate((state.current + 1) % photos.length, false);
      }, speed);
    }

    function pauseForUser() {
      state.userPaused = true;
      if (state.resumeTimer) clearTimeout(state.resumeTimer);
      state.resumeTimer = setTimeout(function () {
        state.userPaused = false;
        sync();
      }, RESUME_AFTER);
      sync();
    }

    state.sync = sync;

    // --------------------------------------------------------- bediening

    if (prevEl) {
      on(prevEl, 'click', function () {
        stepGlobal(-1);
      });
    }
    if (nextEl) {
      on(nextEl, 'click', function () {
        stepGlobal(1);
      });
    }

    dotGroups.forEach(function (group) {
      var groupIndex = parseInt(group.dataset.rvDots, 10);
      Array.prototype.forEach.call(group.querySelectorAll('[data-rv-dot]'), function (dot) {
        on(dot, 'click', function () {
          var first = photoCustomer.indexOf(groupIndex);
          var wanted = parseInt(dot.dataset.rvDot, 10) || 0;
          var last = (countPerCustomer[groupIndex] || 1) - 1;
          activate(first + Math.min(wanted, last), true);
        });
      });
    });

    var mediaCol = root.querySelector('[data-rv-media]');
    if (mediaCol) {
      on(mediaCol, 'pointerenter', function () {
        state.hovered = true;
        sync();
      });
      on(mediaCol, 'pointerleave', function () {
        state.hovered = false;
        sync();
      });
      on(mediaCol, 'focusin', function () {
        state.hovered = true;
        sync();
      });
      on(mediaCol, 'focusout', function () {
        state.hovered = false;
        sync();
      });
    }

    if (typeof reduceMotion.addEventListener === 'function') {
      on(reduceMotion, 'change', sync);
    }

    if ('IntersectionObserver' in window) {
      var visibility = new IntersectionObserver(
        function (entries) {
          state.inView = entries[0].isIntersecting;
          sync();
        },
        { threshold: 0 }
      );
      visibility.observe(root);
      state.observers.push(visibility);
    }

    // ------------------------------------------------------------- kaart

    function buildMap(L) {
      if (state.map || !mapEl) return;

      var center = [toNumber(root.dataset.lat) || 52.153, toNumber(root.dataset.lng) || 6.035];
      var zoom = parseInt(root.dataset.zoom, 10) || 11;
      var tiles = TILES[root.dataset.tiles] || TILES.carto;

      var map = L.map(mapEl, {
        center: center,
        zoom: zoom,
        // Geen scroll-hijacking: het wiel scrollt de pagina, niet de kaart.
        scrollWheelZoom: false,
        // Op touch zou slepen het verticale scrollen van de pagina afvangen.
        dragging: !L.Browser.mobile,
        zoomControl: true,
        attributionControl: true,
      });

      L.tileLayer(tiles.url, tiles.options).addTo(map);

      customers.forEach(function (customer, i) {
        var lat = toNumber(customer.lat);
        var lng = toNumber(customer.lng);
        if (lat === null || lng === null) {
          state.markers[i] = { getElement: function () {}, setZIndexOffset: function () {} };
          return;
        }

        var label = customer.city ? customer.name + ', ' + customer.city : customer.name;
        var marker = L.marker([lat, lng], {
          icon: L.divIcon({
            className: 'rv-pin',
            html: '<span class="rv-pin__dot"></span><span class="rv-pin__label"></span>',
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          }),
          keyboard: true,
          riseOnHover: true,
        }).addTo(map);

        var el = marker.getElement();
        if (el) {
          // textContent, geen HTML: de naam komt uit een instelling.
          el.querySelector('.rv-pin__label').textContent = customer.name || '';
          el.setAttribute('aria-label', label);
          el.setAttribute('aria-current', 'false');
          // Leaflet luistert zelf op Enter; spatie hoort er voor een knop bij.
          el.addEventListener('keydown', function (event) {
            if (event.key === ' ' || event.key === 'Spacebar') {
              event.preventDefault();
              selectCustomer(i);
            }
          });
        }

        marker.on('click', function () {
          selectCustomer(i);
        });

        state.markers[i] = marker;
      });

      state.map = map;

      // De sectie kan tijdens het inladen nog van maat wisselen (fonts, editor).
      if ('ResizeObserver' in window) {
        var resize = new ResizeObserver(function () {
          map.invalidateSize({ animate: false });
        });
        resize.observe(mapEl);
        state.observers.push(resize);
      }

      activate(state.current, false);
    }

    if (mapEl) {
      var start = function () {
        loadLeaflet(root.dataset.leafletCss, root.dataset.leafletJs).then(buildMap).catch(function () {
          // Kaart weg, foto's blijven: de sectie moet ook zonder kaart kloppen.
          mapEl.remove();
        });
      };

      if ('IntersectionObserver' in window) {
        var near = new IntersectionObserver(
          function (entries, obs) {
            if (!entries[0].isIntersecting) return;
            obs.disconnect();
            start();
          },
          { rootMargin: '300px 0px' }
        );
        near.observe(root);
        state.observers.push(near);
      } else {
        start();
      }
    }

    activate(0, false);
    sync();

    root.__rvKlanten = state;
    sections.push(state);
  }

  function destroySection(state) {
    if (state.timer) clearInterval(state.timer);
    if (state.resumeTimer) clearTimeout(state.resumeTimer);
    state.observers.forEach(function (observer) {
      observer.disconnect();
    });
    state.listeners.forEach(function (entry) {
      entry[0].removeEventListener(entry[1], entry[2], entry[3]);
    });
    if (state.map) state.map.remove();
    state.root.removeAttribute('data-rv-ready');
    delete state.root.__rvKlanten;
    sections = sections.filter(function (item) {
      return item !== state;
    });
  }

  function initAll(scope) {
    // Vangnet: mocht shopify:section:unload een keer niet aankomen, dan draaien
    // er anders twee kaarten en twee timers na een herteken-actie.
    sections.slice().forEach(function (state) {
      if (!document.contains(state.root)) destroySection(state);
    });

    var target = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(target.querySelectorAll('[data-rv-klanten]'), initSection);
  }

  window.RvKlantenKaart = { initAll: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    sections.slice().forEach(function (state) {
      if (event.target.contains(state.root)) destroySection(state);
    });
  });

  document.addEventListener('visibilitychange', function () {
    sections.forEach(function (state) {
      state.sync();
    });
  });
})();
